import React, { useEffect, useState } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import api from "../services/api";
import LanguageSwitcher from "../components/LanguageSwitcher";
import AppHandoff from "../components/AppHandoff";

export default function VerifyEmail() {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  // Verifies itself on mount, and the button below is only a fallback.
  //
  // The reason it used to demand a click was email security scanners, which
  // prefetch links and would burn the one-time token before the human arrived.
  // But a scanner fetches the URL; it does not run React. Calling the API from
  // an effect keeps that protection while sparing the person a second click on
  // a page whose only purpose is the click they already made in their inbox.
  const [status, setStatus] = useState<"idle" | "verifying" | "success" | "setup_redirect" | "error">("idle");
  const [errorMessage, setErrorMessage] = useState("");

  useEffect(() => {
    if (!searchParams.get("token")) {
      setStatus("error");
      setErrorMessage(t("auth.no_token", "Verification token is missing"));
    }
  }, [searchParams, t]);

  const handleConfirm = React.useCallback(() => {
    const token = searchParams.get("token");
    if (!token) {
      setStatus("error");
      setErrorMessage(t("auth.no_token", "Verification token is missing"));
      return;
    }
    setStatus("verifying");
    api
      .get(`/auth/verify-email?token=${token}`)
      .then((res) => {
        if (res.data?.setupRequired && res.data?.setupToken) {
          // Club admin flow: redirect to password setup
          setStatus("setup_redirect");
          setTimeout(() => {
            navigate(`/setup-account/${res.data.setupToken}`);
          }, 2000);
        } else {
          setStatus("success");
        }
      })
      .catch((err) => {
        setStatus("error");
        setErrorMessage(err?.response?.data?.error || t("auth.verification_failed", "Verification failed"));
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams, navigate, t]);

  // One click, in the inbox, is enough.
  useEffect(() => {
    if (status === "idle" && searchParams.get("token")) handleConfirm();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status, searchParams, handleConfirm]);

  return (
    <div className="min-h-screen flex">
      {/* Left side - Branding */}
      <div className="hidden lg:flex lg:w-1/2 bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-20 left-20 w-72 h-72 bg-emerald-500 rounded-full filter blur-3xl"></div>
          <div className="absolute bottom-20 right-20 w-96 h-96 bg-blue-500 rounded-full filter blur-3xl"></div>
        </div>
        <div className="relative z-10 flex flex-col justify-center items-center w-full p-12">
          <div className="text-center">
            <h1 className="text-5xl font-bold text-white mb-4">
              <span className="text-emerald-400">i</span>GROWth
            </h1>
            <p className="text-xl text-slate-300 mb-8">
              {t("auth.platform_tagline", "Sports Club Management Platform")}
            </p>
          </div>
        </div>
      </div>

      {/* Right side - Verification Status */}
      <div className="w-full lg:w-1/2 flex flex-col bg-slate-900">
        <div className="absolute top-4 right-4">
          <LanguageSwitcher />
        </div>

        <div className="flex-1 flex items-center justify-center p-8">
          <div className="w-full max-w-md text-center">
            {/* Mobile logo */}
            <div className="lg:hidden text-center mb-8">
              <h1 className="text-3xl font-bold text-white">
                <span className="text-emerald-400">i</span>GROWth
              </h1>
            </div>

            <div className="bg-slate-800/50 backdrop-blur-sm rounded-2xl p-8 border border-slate-700/50 shadow-xl">
              {status === "idle" && (
                <>
                  <div className="w-16 h-16 mx-auto mb-6 rounded-full bg-emerald-500/20 flex items-center justify-center">
                    <svg className="w-8 h-8 text-emerald-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                    </svg>
                  </div>
                  <h2 className="text-2xl font-semibold text-white mb-4">
                    {t("auth.confirm_email_title", "Confirmă adresa de email")}
                  </h2>
                  <p className="text-slate-400 mb-6">
                    {t("auth.confirm_email_desc", "Dacă nu pornește singură, apasă butonul de mai jos.")}
                  </p>
                  <button
                    onClick={handleConfirm}
                    className="inline-block w-full py-3 px-4 bg-emerald-600 hover:bg-emerald-500 text-white font-medium rounded-lg transition-all duration-200"
                  >
                    {t("auth.confirm_email_button", "Confirmă adresa de email")}
                  </button>
                </>
              )}

              {status === "verifying" && (
                <>
                  <div className="w-16 h-16 mx-auto mb-6 rounded-full bg-blue-500/20 flex items-center justify-center">
                    <svg className="w-8 h-8 text-blue-400 animate-spin" fill="none" viewBox="0 0 24 24">
                      <circle
                        className="opacity-25"
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        strokeWidth="4"
                      ></circle>
                      <path
                        className="opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                      ></path>
                    </svg>
                  </div>
                  <h2 className="text-2xl font-semibold text-white mb-4">
                    {t("auth.verifying_email", "Verifying your email...")}
                  </h2>
                  <p className="text-slate-400">
                    {t("auth.please_wait", "Please wait while we verify your email address.")}
                  </p>
                </>
              )}

              {status === "setup_redirect" && (
                <>
                  <div className="w-16 h-16 mx-auto mb-6 rounded-full bg-blue-500/20 flex items-center justify-center">
                    <svg className="w-8 h-8 text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                  </div>
                  <h2 className="text-2xl font-semibold text-white mb-4">
                    {t("auth.email_verified", "Email verificat!")}
                  </h2>
                  <p className="text-slate-400 mb-6">
                    {t("auth.setup_redirect_message", "Emailul a fost verificat cu succes. Te redirecționăm către pagina de setare a parolei...")}
                  </p>
                  <div className="w-8 h-8 mx-auto border-4 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
                </>
              )}

              {status === "success" && (
                <>
                  <div className="w-16 h-16 mx-auto mb-6 rounded-full bg-emerald-500/20 flex items-center justify-center">
                    <svg className="w-8 h-8 text-emerald-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                  </div>
                  <h2 className="text-2xl font-semibold text-white mb-4">
                    {t("auth.email_verified", "Email Verified!")}
                  </h2>
                  <p className="text-slate-400 mb-6">
                    {t(
                      "auth.email_verified_message",
                      "Adresa ta e confirmată. Mai departe, totul se întâmplă în aplicație — instaleaz-o și intră direct cu datele tale."
                    )}
                  </p>
                  {/* They verified in a browser because that is where email
                      links go; the app is where they actually live from here
                      on, so offer it now rather than leaving them to find it. */}
                  <AppHandoff
                    path="/app"
                    title={t("auth.install_app", "Instalează aplicația")!}
                    subtitle={t(
                      "auth.install_app_sub",
                      "Alege magazinul telefonului tău. Te autentifici cu aceleași date."
                    )!}
                  />
                  <Link
                    to="/login"
                    className="inline-block w-full py-3 px-4 mt-4 bg-emerald-600 hover:bg-emerald-500 text-white font-medium rounded-lg transition-all duration-200"
                  >
                    {t("auth.go_to_login", "Go to Login")}
                  </Link>
                </>
              )}

              {status === "error" && (
                <>
                  <div className="w-16 h-16 mx-auto mb-6 rounded-full bg-red-500/20 flex items-center justify-center">
                    <svg className="w-8 h-8 text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </div>
                  <h2 className="text-2xl font-semibold text-white mb-4">
                    {t("auth.verification_error", "Verification Failed")}
                  </h2>
                  <p className="text-slate-400 mb-6">{errorMessage}</p>
                  <div className="space-y-3">
                    <Link
                      to="/login"
                      className="inline-block w-full py-3 px-4 bg-slate-700 hover:bg-slate-600 text-white font-medium rounded-lg transition-all duration-200"
                    >
                      {t("auth.go_to_login", "Go to Login")}
                    </Link>
                    <Link
                      to="/register"
                      className="inline-block w-full py-3 px-4 bg-emerald-600 hover:bg-emerald-500 text-white font-medium rounded-lg transition-all duration-200"
                    >
                      {t("auth.try_again", "Register Again")}
                    </Link>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
